import React, { useState, useEffect } from 'react';
import { reporteService } from '../services/api';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import * as XLSX from 'xlsx';
import '../styles/Reportes.css';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d', '#ffc658', '#ff7c7c'];

const Reportes = () => {
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cargarReportes();
  }, []);

  const cargarReportes = async () => {
    try {
      const response = await reporteService.getGeneral();
      setDatos(response.data);
    } catch (error) {
      console.error('Error al cargar reportes:', error);
      alert('Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = () => {
    if (!datos) return;

    // Crear libro de Excel
    const wb = XLSX.utils.book_new();

    // Hoja 1: Resumen General
    const resumenData = [
      ['RESUMEN GENERAL'],
      [''],
      ['Total Proyectos', datos.totales_generales.total_proyectos],
      ['Proyectos Completados', datos.totales_generales.proyectos_completados],
      ['Proyectos En Curso', datos.totales_generales.proyectos_en_curso],
      ['Presupuesto Total', parseFloat(datos.totales_generales.presupuesto_total).toFixed(2)],
      ['Gastos Totales', parseFloat(datos.totales_generales.gastos_totales).toFixed(2)],
      [''],
      ['TRABAJADORES'],
      ['Total Trabajadores', datos.resumen_trabajadores.total_trabajadores],
      ['Trabajadores Activos', datos.resumen_trabajadores.trabajadores_activos],
      ['Asignaciones Activas', datos.resumen_trabajadores.total_asignaciones_activas]
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(resumenData);
    XLSX.utils.book_append_sheet(wb, ws1, 'Resumen General');

    // Hoja 2: Proyectos por Estado
    const proyectosEstadoData = [
      ['Estado', 'Total', 'Presupuesto Total']
    ];
    datos.proyectos_por_estado.forEach(item => {
      proyectosEstadoData.push([
        item.estado,
        item.total,
        parseFloat(item.presupuesto_total).toFixed(2)
      ]);
    });
    const ws2 = XLSX.utils.aoa_to_sheet(proyectosEstadoData);
    XLSX.utils.book_append_sheet(wb, ws2, 'Proyectos por Estado');

    // Hoja 3: Detalle de Proyectos
    const proyectosData = [
      ['Número', 'Nombre', 'Cliente', 'Estado', 'Presupuesto', 'Gastos', 'Avance %', 'Fecha Inicio']
    ];
    datos.proyectos_detalle.forEach(p => {
      proyectosData.push([
        p.numero_proyecto,
        p.nombre,
        p.cliente_nombre,
        p.estado,
        parseFloat(p.presupuesto_asignado || 0).toFixed(2),
        parseFloat(p.monto_total_gastos || 0).toFixed(2),
        p.avance_actual || 0,
        p.fecha_inicio ? new Date(p.fecha_inicio).toLocaleDateString() : 'N/A'
      ]);
    });
    const ws3 = XLSX.utils.aoa_to_sheet(proyectosData);
    XLSX.utils.book_append_sheet(wb, ws3, 'Detalle Proyectos');

    // Hoja 4: Gastos por Especialidad
    const gastosEspecialidadData = [
      ['Especialidad', 'Total Gastos', 'Monto Total']
    ];
    datos.gastos_por_especialidad.forEach(item => {
      gastosEspecialidadData.push([
        item.especialidad,
        item.total_gastos,
        parseFloat(item.monto_total).toFixed(2)
      ]);
    });
    const ws4 = XLSX.utils.aoa_to_sheet(gastosEspecialidadData);
    XLSX.utils.book_append_sheet(wb, ws4, 'Gastos por Especialidad');

    // Hoja 5: Gastos por Tipo de Documento
    const gastosTipoData = [
      ['Tipo Documento', 'Total', 'Monto Total']
    ];
    datos.gastos_por_tipo.forEach(item => {
      gastosTipoData.push([
        item.tipo_documento,
        item.total,
        parseFloat(item.monto_total).toFixed(2)
      ]);
    });
    const ws5 = XLSX.utils.aoa_to_sheet(gastosTipoData);
    XLSX.utils.book_append_sheet(wb, ws5, 'Gastos por Tipo');

    // Guardar archivo
    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Reporte_AppContra_${fecha}.xlsx`);
  };

  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(valor);
  };

  if (loading) {
    return <div className="loading">Cargando reportes...</div>;
  }

  if (!datos) {
    return <div className="error-message">Error al cargar datos de reportes</div>;
  }

  // Preparar datos para gráficos
  const datosProyectosPorEstado = datos.proyectos_por_estado.map(item => ({
    name: item.estado.replace('_', ' '),
    value: parseInt(item.total),
    presupuesto: parseFloat(item.presupuesto_total)
  }));

  const datosGastosPorEspecialidad = datos.gastos_por_especialidad.map(item => ({
    name: item.especialidad,
    gastos: parseInt(item.total_gastos),
    monto: parseFloat(item.monto_total)
  }));

  const datosGastosPorTipo = datos.gastos_por_tipo.map(item => ({
    name: item.tipo_documento,
    value: parseFloat(item.monto_total)
  }));

  const datosAvanceProyectos = datos.proyectos_detalle
    .filter(p => p.avance_actual)
    .map(p => ({
      name: p.numero_proyecto,
      avance: parseFloat(p.avance_actual || 0)
    }))
    .slice(0, 10); // Mostrar últimos 10 proyectos

  return (
    <div className="reportes-container">
      <div className="reportes-header">
        <h1>Reportes y Estadísticas</h1>
        <button className="btn btn-success" onClick={exportarExcel}>
          📊 Exportar a Excel
        </button>
      </div>

      {/* Tarjetas de Resumen */}
      <div className="resumen-cards">
        <div className="resumen-card">
          <div className="card-icon">🏗️</div>
          <div className="card-content">
            <h3>Total Proyectos</h3>
            <p className="card-value">{datos.totales_generales.total_proyectos}</p>
            <div className="card-details">
              <span>✅ {datos.totales_generales.proyectos_completados} Completados</span>
              <span>🔨 {datos.totales_generales.proyectos_en_curso} En Curso</span>
            </div>
          </div>
        </div>

        <div className="resumen-card">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <h3>Presupuesto Total</h3>
            <p className="card-value">{formatearMoneda(datos.totales_generales.presupuesto_total)}</p>
          </div>
        </div>

        <div className="resumen-card">
          <div className="card-icon">💸</div>
          <div className="card-content">
            <h3>Gastos Totales</h3>
            <p className="card-value">{formatearMoneda(datos.totales_generales.gastos_totales)}</p>
            <p className="card-subtitle">
              {((parseFloat(datos.totales_generales.gastos_totales) / parseFloat(datos.totales_generales.presupuesto_total)) * 100).toFixed(1)}% del presupuesto
            </p>
          </div>
        </div>

        <div className="resumen-card">
          <div className="card-icon">👷</div>
          <div className="card-content">
            <h3>Trabajadores</h3>
            <p className="card-value">{datos.resumen_trabajadores.total_trabajadores}</p>
            <div className="card-details">
              <span>✓ {datos.resumen_trabajadores.trabajadores_activos} Activos</span>
              <span>📋 {datos.resumen_trabajadores.total_asignaciones_activas} Asignaciones</span>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="graficos-grid">
        {/* Gráfico de Proyectos por Estado */}
        <div className="grafico-card">
          <h2>Proyectos por Estado</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={datosProyectosPorEstado}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {datosProyectosPorEstado.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfico de Gastos por Especialidad */}
        <div className="grafico-card">
          <h2>Gastos por Especialidad</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={datosGastosPorEspecialidad}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip formatter={(value) => formatearMoneda(value)} />
              <Legend />
              <Bar dataKey="monto" fill="#0088FE" name="Monto Total" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfico de Gastos por Tipo de Documento */}
        <div className="grafico-card">
          <h2>Gastos por Tipo de Documento</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={datosGastosPorTipo}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${formatearMoneda(value)}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {datosGastosPorTipo.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatearMoneda(value)} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfico de Avance de Proyectos */}
        {datosAvanceProyectos.length > 0 && (
          <div className="grafico-card">
            <h2>Avance de Proyectos Recientes</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={datosAvanceProyectos}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="avance" stroke="#00C49F" strokeWidth={2} name="% Avance" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Tabla de Proyectos */}
      <div className="tabla-card">
        <h2>Detalle de Proyectos</h2>
        <div className="tabla-responsive">
          <table className="tabla-reportes">
            <thead>
              <tr>
                <th>Número</th>
                <th>Nombre</th>
                <th>Cliente</th>
                <th>Estado</th>
                <th>Presupuesto</th>
                <th>Gastos</th>
                <th>Saldo</th>
                <th>Avance</th>
              </tr>
            </thead>
            <tbody>
              {datos.proyectos_detalle.map(proyecto => {
                const presupuesto = parseFloat(proyecto.presupuesto_asignado || 0);
                const gastos = parseFloat(proyecto.monto_total_gastos || 0);
                const saldo = presupuesto - gastos;

                return (
                  <tr key={proyecto.id}>
                    <td>{proyecto.numero_proyecto}</td>
                    <td>{proyecto.nombre}</td>
                    <td>{proyecto.cliente_nombre}</td>
                    <td>
                      <span className={`estado-badge ${proyecto.estado}`}>
                        {proyecto.estado.replace('_', ' ')}
                      </span>
                    </td>
                    <td>{formatearMoneda(presupuesto)}</td>
                    <td>{formatearMoneda(gastos)}</td>
                    <td className={saldo < 0 ? 'texto-rojo' : 'texto-verde'}>
                      {formatearMoneda(saldo)}
                    </td>
                    <td>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${proyecto.avance_actual || 0}%` }}
                        >
                          {proyecto.avance_actual || 0}%
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Reportes;
